import { call, put, select, all } from 'redux-saga/effects';
import { produce } from 'immer';
import * as actions from './actions';
import axios from 'axios';
import {
  flattenExpressionForRequest,
  mapExpressionFromResposne
} from 'store/helpers';
import * as eventSagas from 'store/event/sagas';
const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;
const { updateConditionQueryFilters } = actions;
import { getParams } from 'store/helpers/helperFunctions';

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;

const getTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/condition-query`;

let isDeleteInProgress = false;

export const getPaginatedTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getMinimalConditionQueryTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const createTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .post(`${getTemplateApiRoot(tenantId, businessContextId)}`, template)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${
        template.uuid
      }`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTemplateAsync = (tenantId, businessContextId, templateId) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${templateId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteTemplateAsync = (tenantId, businessContextId, templateId) =>
  axios
    .delete(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${templateId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getConditionQueriesAsync = (
  tenantId,
  businessContextId,
  campaign,
  urlParams
) =>
  axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      campaign
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentTemplate = store =>
  store.conditionQueryTemplate ? store.conditionQueryTemplate.current : {};
const paginatedTemplateList = store =>
  store.conditionQueryTemplate
    ? store.conditionQueryTemplate.paginatedList
    : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const searchInput = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.searchInput : '';
const sortBy = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.sortBy : 'name';
const sortOrder = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.pageNo : 0;
const totalCount = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.totalCount : 0;
const filterTemplates = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.conditionQueryTemplate?.filters ? store.conditionQueryTemplate?.filters?.filterProductCategories : [];
const conditionExpression = store =>
  store.conditionQueryTemplate
    ? store.conditionQueryTemplate.conditionExpression
    : [];

export function* getPaginatedTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedTemplateList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);
    if (
      action.type === actions.GET_PAGINATED_CONDITION_QUERY_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        searchScopes: yield select(filterTemplates),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories)
      };

      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isDeleteInProgress &&
        listCount - 1 >= 0 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateConditionQueryFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedConditionQueryTemplates());
      currentList = yield call(
        getPaginatedTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(updateConditionQueryFilters({ totalCount: currentList.totalRecords }));
    }
    isDeleteInProgress = false;
    yield put(
      actions.gettingPaginatedConditionQueryTemplatesSucceeded(currentList)
    );
  } catch (ex) {
    isDeleteInProgress = false;
    yield put(updateConditionQueryFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedConditionQueryTemplatesFailed(ex));
  }
}

export function* getMinimalConditionQueryTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    
    if (action.type === actions.GET_MINIMAL_CONDITION_QUERY_TEMPLATES) {
      yield put(actions.gettingMinimalConditionQueryTemplates());
      
      let urlParams = {
        index: payload.index || 0,
        pageSize: payload.pageSize || 100
      };
      
      let bodyParams = {
        active: payload.active !== undefined ? payload.active : true,
        ...(payload.productCategory && { productCategory: payload.productCategory }),
        ...(payload.productName && { productName: payload.productName }),
        ...(payload.eventTypeCategory && { eventTypeCategory: payload.eventTypeCategory }),
        ...(payload.name && { name: payload.name })
      };
      
      let currentList = yield call(
        getMinimalConditionQueryTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      
      yield put(actions.gettingMinimalConditionQueryTemplatesSucceeded({
        data: currentList.data,
        totalRecords: currentList.totalRecords
      }));
    }
  } catch (ex) {
    yield put(actions.gettingMinimalConditionQueryTemplatesFailed(ex));
  }
}

export function* getTemplateIfNeeded(action) {
  const { payload = {} } = action;
  try {
    yield put(actions.gettingConditionQueryTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    let template = yield select(currentTemplate);
    let templateId = payload.templateId || payload.templateUuid;

    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes)
    };

    if (template.uuid !== templateId || payload.force === true) {
      template = yield call(
        getTemplateAsync,
        tenantId,
        businessContextId,
        templateId
      );
    }

    let eventMetaData;
    let eventFields = [];
    if (template.schemaIdentifier && template.productName) {
      yieldAlls.eventMetaData = yield call(waitForGetEventMetaData, {
        schemaIdentifier: template.schemaIdentifier,
        productName: template.productName,
        workflow: 'campaign',
        productType: template.productType
      });
      ({ eventMetaData: { eventMetaData } = {} } = yield all(yieldAlls));
      eventMetaData.map(data =>
        eventFields.push(...data.schemaConditionAttributes)
      );
    }

    if (
      template.uuid ||
      (template.hasOwnProperty('_hasSetTemplate') && !template._hasSetTemplate)
    ) {
      template = produce(template, draftTemplate => {
        draftTemplate.conditionExpression = mapExpressionFromResposne(
          draftTemplate?.conditionExpression || {},
          eventFields
        );
        if (draftTemplate.hasOwnProperty('_hasSetTemplate')) {
          draftTemplate._hasSetTemplate = true;
        }
        draftTemplate.hasMovedToNextStep = true;
      });
    }

    yield put(
      actions.gettingConditionQueryTemplateSucceeded({
        template,
        eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.gettingConditionQueryTemplateFailed(ex));
  }
}

export function* createTemplateFromCurrent() {
  try {
    yield put(actions.creatingConditionQueryTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let conditionExpression = template.conditionExpression;
    let metaData = template.eventMetaData;

    template = produce(template, thisTemplate => {
      thisTemplate.conditionExpression = flattenExpressionForRequest(
        thisTemplate.conditionExpression
      );
    });
    if (!template.uuid) {
      template = yield call(
        createTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    template = {
      ...template,
      conditionExpression: conditionExpression,
      eventMetaData: metaData,
      hasMovedToNextStep: true
    };
    yield put(actions.creatingConditionQueryTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.creatingConditionQueryTemplateFailed(ex));
  }
}

export function* updateTemplateFromCurrent() {
  try {
    yield put(actions.updatingConditionQueryTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let conditionExpression = template.conditionExpression;
    let metaData = template.eventMetaData;

    template = produce(template, thisTemplate => {
      thisTemplate.conditionExpression = flattenExpressionForRequest(
        thisTemplate.conditionExpression
      );
    });

    if (template.uuid) {
      template = yield call(
        updateTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    template = {
      ...template,
      conditionExpression: conditionExpression,
      eventMetaData: metaData,
      hasMovedToNextStep: true
    };

    yield put(actions.updatingConditionQueryTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.updatingConditionQueryTemplateFailed(ex));
  }
}

export function* getConditionQueries(action) {
  const { payload = {} } = action;

  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let conditionQueries = yield select(conditionExpression);
    let urlParams = {
      index: 0,
      pageSize: 999
    };
    if (
      payload.force ||
      conditionQueries?.length === 0 ||
      conditionQueries[0].schemaIdentifier !== payload?.data?.schemaIdentifier
    ) {
      conditionQueries = yield call(
        getConditionQueriesAsync,
        tenantId,
        businessContextId,
        payload.data || {},
        urlParams
      );
    }
    yield put(actions.gettingConditionQueriesSucceeded(conditionQueries));
  } catch (ex) {
    yield put(actions.gettingConditionQueriesFailed());
  }
}

export function* deleteConditionQuery(action) {
  const { payload = {} } = action;

  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    yield call(deleteTemplateAsync, tenantId, businessContextId, payload);
    isDeleteInProgress = true;
    yield put(actions.deletingConditionQuerySucceeded());
  } catch (ex) {
    isDeleteInProgress = false;
    yield put(actions.deletingConditionQueryFailed(ex));
  }
}
